import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatCents } from '../../../lib/money'
import { LegendDot } from './LegendDot'

// Colores estructura balance
const BAL_AC = '#22d3ee' // cyan-400
const BAL_ANC = '#0891b2' // cyan-600
const BAL_PC = '#dc2626' // red-600
const BAL_PNC = '#991b1b' // red-800
const BAL_PN = '#16a34a' // emerald-600

interface BalanceVarsLike {
  AC: number
  ANC: number
  PC: number
  PNC: number
  PN: number
}

export function BalanceStructureChart({ vars: v }: { vars: BalanceVarsLike }) {
  const { t } = useTranslation('accounting')
  const totalActivo = v.AC + v.ANC
  const totalPasPn = v.PC + v.PNC + v.PN

  const chartData = useMemo(() => {
    return [
      {
        side: t('balance.activo'),
        AC: Math.max(0, v.AC),
        ANC: Math.max(0, v.ANC),
        PC: 0,
        PNC: 0,
        PN: 0,
        total: totalActivo,
      },
      {
        side: t('balance.pasivoPn'),
        AC: 0,
        ANC: 0,
        PC: Math.max(0, v.PC),
        PNC: Math.max(0, v.PNC),
        PN: Math.max(0, v.PN),
        total: totalPasPn,
      },
    ]
  }, [v, totalActivo, totalPasPn, t])

  return (
    <section className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
      <header className="flex items-center justify-between mb-3">
        <h2 className="font-semibold t-primary">{t('balance.title')}</h2>
        <span className="text-xs t-muted">
          {formatCents(totalActivo)} vs {formatCents(totalPasPn)}
        </span>
      </header>
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            layout="vertical"
            stackOffset="expand"
            margin={{ top: 10, right: 16, bottom: 10, left: 80 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--border)"
              strokeOpacity={0.35}
              vertical={false}
            />
            <XAxis
              tickLine={false}
              axisLine={false}
              type="number"
              tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
              stroke="var(--text-muted, #64748b)"
              fontSize={11}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              type="category"
              dataKey="side"
              stroke="var(--text-secondary, #94a3b8)"
              fontSize={12}
            />
            <Tooltip content={<BalanceTooltip />} cursor={{ fill: 'var(--bg-hover)' }} />
            <Bar dataKey="AC" stackId="bal" fill={BAL_AC} radius={[6, 0, 0, 6]}>
              <LabelList content={<PctLabel total="total" labelText="AC" />} />
            </Bar>
            <Bar dataKey="ANC" stackId="bal" fill={BAL_ANC} radius={[0, 6, 6, 0]}>
              <LabelList content={<PctLabel total="total" labelText="ANC" />} />
            </Bar>
            <Bar dataKey="PC" stackId="bal" fill={BAL_PC} radius={[6, 0, 0, 6]}>
              <LabelList content={<PctLabel total="total" labelText="PC" />} />
            </Bar>
            <Bar dataKey="PNC" stackId="bal" fill={BAL_PNC}>
              <LabelList content={<PctLabel total="total" labelText="PNC" />} />
            </Bar>
            <Bar dataKey="PN" stackId="bal" fill={BAL_PN} radius={[0, 6, 6, 0]}>
              <LabelList content={<PctLabel total="total" labelText="PN" />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-xs t-secondary">
        <LegendDot color={BAL_AC} label={t('balance.ac')} />
        <LegendDot color={BAL_ANC} label={t('balance.anc')} />
        <LegendDot color={BAL_PC} label={t('balance.pc')} />
        <LegendDot color={BAL_PNC} label={t('balance.pnc')} />
        <LegendDot color={BAL_PN} label={t('balance.pn')} />
      </div>
    </section>
  )
}

interface BalanceTooltipPayloadItem {
  dataKey: string
  value: number
  color: string
  name: string
  payload: { side: string; total: number }
}

function BalanceTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: BalanceTooltipPayloadItem[]
}) {
  const { t } = useTranslation('accounting')
  if (!active || !payload || payload.length === 0) return null
  const row = payload[0]?.payload
  if (!row) return null
  const total = row.total || 1
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-input)] p-3 text-xs shadow-lg">
      <div className="font-semibold t-primary mb-2">{row.side}</div>
      {payload
        .filter((p) => p.value > 0)
        .map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-3 t-secondary">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: p.color }} />
              {p.dataKey}
            </span>
            <span className="font-mono">
              {formatCents(p.value)} ({((p.value / total) * 100).toFixed(1)}%)
            </span>
          </div>
        ))}
      <div className="mt-2 pt-2 border-t border-[var(--border)] flex justify-between t-primary font-medium">
        <span>{t('total')}</span>
        <span className="font-mono">{formatCents(row.total)}</span>
      </div>
    </div>
  )
}

interface PctLabelProps {
  total: string
  labelText: string
  x?: number
  y?: number
  width?: number
  height?: number
  value?: number
  // Recharts también pasa el payload entero:
  payload?: { [k: string]: unknown }
}

function PctLabel(props: PctLabelProps) {
  const { x = 0, y = 0, width = 0, height = 0, value = 0, labelText } = props
  if (!value || width < 32) return null
  // Recharts pasa `value` ya en porcentaje fraccional para layout="vertical"
  // con stackOffset="expand" — pero por seguridad reconstruimos desde el ancho.
  const cx = Number(x) + Number(width) / 2
  const cy = Number(y) + Number(height) / 2
  return (
    <text
      x={cx}
      y={cy}
      fill="#fff"
      fontSize={11}
      fontWeight={600}
      textAnchor="middle"
      dominantBaseline="middle"
      pointerEvents="none"
    >
      {labelText}
    </text>
  )
}
